import { manageContentPanel as manageContentPanelDef } from '@wix/wix-data-client-common/dist/esm/panels/panelDefs'
import getPanelUrl from '@wix/wix-data-client-common/dist/esm/panels/getPanelUrl'
import { getComponentTypeData } from '../../business-logic/components/getComponentTypeData'
import type {
  EditorSDK,
  Logger,
  SafeUserPreferences,
} from '@wix/wix-data-client-common-standalone'
import type { I18nextProviderProps } from 'react-i18next'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type { ComponentRef } from '@wix/editor-platform-sdk-types'
import type { AppApi, EDITOR_TYPE } from '@wix/wix-data-client-common'
import type { InitialData } from './types'
import {
  GFPP_AUTO_OPEN_ORIGIN,
  GFPP_CHANGE_CONTENT_ORIGIN,
  openAutoCms,
  openMultilingual,
} from './panelActions'
import {
  CollectionIds,
  openCollectionDirectlyPreferenceKey,
  openMultilingualDirectlyPreferenceKey,
} from './constants'

interface OpenPanelProps {
  appApi: AppApi
  collectionsApi: CollectionsApi
  componentRef: ComponentRef
  editorSdkProxy: EditorSDK
  hostname: string
  i18n: I18nextProviderProps['i18n']
  role: string
  safeUserPreferences: SafeUserPreferences
  logger: Logger
  editorType: EDITOR_TYPE
}

const getMultilingualLanguages = async ({
  editorSdkProxy,
}: {
  editorSdkProxy: EditorSDK
}): Promise<null | { primaryLanguage: string; currentLanguage: string }> => {
  if (!(await editorSdkProxy.document.language.multilingual.isEnabled())) {
    return null
  }
  const [{ code: originalLanguage }, currentLanguage] = await Promise.all([
    editorSdkProxy.document.language.original.get(),
    editorSdkProxy.document.language.current.get(),
  ])
  if (originalLanguage === currentLanguage) {
    return null
  }
  return {
    primaryLanguage: originalLanguage,
    currentLanguage,
  }
}

export const openPanel = async ({
  appApi,
  collectionsApi,
  componentRef,
  editorSdkProxy,
  hostname,
  i18n,
  logger,
  role,
  safeUserPreferences,
  editorType,
}: OpenPanelProps) => {
  const dataset = await appApi.fetchConnectedDataset(componentRef)

  const {
    config: { collectionId },
  } = dataset

  const collectionName = await collectionsApi
    .get(collectionId)
    .then(collectionSchema => collectionSchema.displayName)
    .catch(() => collectionId)

  const multilingualLanguages = await getMultilingualLanguages({
    editorSdkProxy,
  })

  const preferences = await safeUserPreferences.get(
    [
      openCollectionDirectlyPreferenceKey,
      openMultilingualDirectlyPreferenceKey,
    ],
    {
      [openCollectionDirectlyPreferenceKey]: false,
      [openMultilingualDirectlyPreferenceKey]: false,
    },
  )

  if (multilingualLanguages) {
    if (preferences[openMultilingualDirectlyPreferenceKey]) {
      openMultilingual({
        collectionId,
        collectionName,
        multilingualLanguages,
        origin: GFPP_AUTO_OPEN_ORIGIN,
        logger,
        editorSdkProxy,
      })
      return
    }
  } else {
    if (
      preferences[openCollectionDirectlyPreferenceKey] ||
      collectionId === CollectionIds.INQUIRY_SERVICES
    ) {
      openAutoCms({
        editorSdkProxy,
        componentRef,
        appApi,
        origin: GFPP_CHANGE_CONTENT_ORIGIN,
      })
      return
    }
  }

  const { sdkType: componentType } = await getComponentTypeData({
    editorSdkProxy,
    componentRef,
  })

  const bindingDefinition = await appApi.getBindingDefinition({
    componentType,
  })

  const manageContentPanelConfig = bindingDefinition.sections.find(
    section => role === section.role,
  )?.manageContentPanel

  const isElementNamePlural =
    manageContentPanelConfig?.isElementNamePlural ?? false

  const elementName = bindingDefinition.componentName
    ? i18n.t(bindingDefinition.componentName)
    : ''

  const title = manageContentPanelConfig?.alternativeTitle
    ? i18n.t(manageContentPanelConfig.alternativeTitle)
    : i18n.t('Manage_Content_Panel_V2_Title', {
        elementName,
      })

  const initialData: InitialData = {
    collectionId,
    collectionName,
    elementName,
    isElementNamePlural,
    componentRef,
    multilingualLanguages,
    controllerRef: dataset.controllerRef,
    editorType,
  }

  editorSdkProxy.editor.openComponentPanel({
    url: await getPanelUrl({
      hostname,
      relativeUrl: manageContentPanelDef.url,
      editorSdkProxy,
    }),
    title,
    initialData,
    width: 288,
    /**
     * here we are setting the height a few pixels higher than the height of content
     * in order to avoid scrollbar when the panel is opened for rich content component
     */
    height: 358,
    componentRef,
    helpId: i18n.t('Manage_Content_Panel_Help_ID'),
  })
}
